import React, { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'

import {
  addIngredientsToFoodGroup,
  deleteFoodGroup,
  foodGroupIngredientsQuery,
  foodGroupQuery,
  removeIngredientFromFoodGroup,
  saveFoodGroup,
} from '@/api'
import { FoodGroup, SearchIngredient } from '@/api/types'
import EditFoodGroup from '@/features/foodgroups/EditFoodGroup'

export const Route = createFileRoute('/foodgroups/$groupId')({
  loader: async ({ context: { queryClient }, params }) => {
    const [foodGroup, ingredients] = await Promise.all([
      queryClient.ensureQueryData(foodGroupQuery(params.groupId)),
      queryClient.ensureQueryData(foodGroupIngredientsQuery(params.groupId)),
    ])

    return { foodGroup, ingredients }
  },

  component: FoodGroupComponent,
})

function FoodGroupComponent() {
  const navigate = useNavigate()
  const { foodGroup, ingredients } = Route.useLoaderData()
  const [tempIngredients, setTempIngredients] = useState(ingredients)

  const saveMutation = useMutation({
    mutationFn: (group: FoodGroup) => saveFoodGroup(group),
    onSuccess: () => {
      toast.success('Food group saved with success')
    },
    onError: () => {
      toast.error('Error saving food group')
    },
  })

  const deleteIngredient = useMutation({
    mutationFn: (idIngredient: number) =>
      removeIngredientFromFoodGroup(foodGroup.idGroup, idIngredient),
    onSuccess: (_, idIngredient) => {
      setTempIngredients(tempIngredients.filter((ing) => ing.idIngredient !== idIngredient))
    },
    onError: () => {
      toast.error('Error deleting ingredient')
    },
  })

  const addIngredients = useMutation({
    mutationFn: (ings: SearchIngredient[]) => addIngredientsToFoodGroup(foodGroup, ings),
    onSuccess: (_, ings) => {
      setTempIngredients(ings)
    },
  })

  const deleteGroup = useMutation({
    mutationFn: () => deleteFoodGroup(foodGroup.idGroup),
    onSuccess: () => {
      toast.success('Deleted with success')

      navigate({
        to: '/foodgroups',
      })
    },
    onError: () => {
      toast.error('Error deleting food group')
    },
  })

  const handleDeleteGroup = () => {
    if (foodGroup.knownFoodGroupType) {
      alert('Groups with a type known by the system cannot be deleted')
    } else if (confirm('Are you sure you want to delete this food group?')) {
      deleteGroup.mutate()
    }
  }

  const handleDeleteIngredient = (idIngredient: number) => {
    deleteIngredient.mutate(idIngredient)
  }

  const handleIngredients = (ings: SearchIngredient[]) => {
    addIngredients.mutate(ings)
  }

  const handleSave = (name: string) => {
    saveMutation.mutate({
      ...foodGroup,
      name,
    })
  }

  return (
    <EditFoodGroup
      foodGroup={foodGroup}
      rows={tempIngredients}
      onAddIngredients={handleIngredients}
      onSaveGroup={handleSave}
      onDeleteGroup={handleDeleteGroup}
      onDeleteIngredient={handleDeleteIngredient}
    />
  )
}
